// src/cbt_lms/participant_login.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Helmet } from 'react-helmet-async';
import Cookies from 'universal-cookie';
import { LoginUrl, gmailLogin, BasedUrl } from './serverurl';

const ParticipantLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [processing, setProcessing] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passcode, setPasscode] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingPasscode, setPendingPasscode] = useState('');
  const [modalEmail, setModalEmail] = useState('');
  const [modalPassword, setModalPassword] = useState('');
  const [modalFormErrors, setModalFormErrors] = useState({});
  const [modalType, setModalType] = useState('passcode');
  const cookies = new Cookies();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-detect passcode from URL
  useEffect(() => {
    const urlPasscode = searchParams.get('cbthost-passcode');
    if (urlPasscode) {
      setPasscode(urlPasscode);
      validatePasscodeFromUrl(urlPasscode);
    }
  }, [searchParams]);

  const setSessionCookies = (responseData, userType) => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + 7);
    cookies.set('auto_login_code', responseData.auto_login_code, { expires: expiryDate });
    cookies.set('auth_key', responseData.access_token, { expires: expiryDate });
    cookies.set('access_token', responseData.access_token, { expires: expiryDate });
    cookies.set('name', responseData.name, { expires: expiryDate });
    cookies.set('email', responseData.email, { expires: expiryDate });
    cookies.set('authenticated', true, { expires: expiryDate });
    cookies.set('user_type', userType, { expires: expiryDate });
    if (responseData.user_id) cookies.set('user_id', responseData.user_id, { expires: expiryDate });
  };

  const handleParticipantLoginSuccess = (responseData, redirectToTest = false) => {
    setSessionCookies(responseData, 'participant');
    toast.success('Welcome!');
    if (redirectToTest && pendingPasscode) {
      navigate(`/participate/verification?cbt_pass_code=${pendingPasscode}`);
      setShowLoginModal(false);
      setPendingPasscode('');
    } else {
      navigate('/participate/dashboard');
    }
  };

  const validatePasscodeFromUrl = async (code) => {
    setProcessing(true);
    try {
      await axios.get(`${BasedUrl}cbt-route/pass-code/${code.toUpperCase()}`);
      const isAuth = cookies.get('authenticated') && cookies.get('user_type') === 'participant';
      if (isAuth) {
        navigate(`/participate/verification?cbt_pass_code=${code}`);
      } else {
        setPendingPasscode(code);
        setModalType('passcode');
        setShowLoginModal(true);
      }
    } catch {
      toast.error('Invalid passcode');
    } finally {
      setProcessing(false);
    }
  };

  const handleParticipate = async (e) => {
    e.preventDefault();
    if (!passcode.trim()) {
      setFormErrors({ passcode: 'Passcode required' });
      return;
    }
    setProcessing(true);
    try {
      await axios.get(`${BasedUrl}cbt-route/pass-code/${passcode.toUpperCase()}`);
      const isAuth = cookies.get('authenticated') && cookies.get('user_type') === 'participant';
      if (isAuth) {
        navigate(`/participate/verification?cbt_pass_code=${passcode}`);
      } else {
        setPendingPasscode(passcode);
        setModalType('passcode');
        setShowLoginModal(true);
      }
    } catch {
      toast.error('Invalid passcode');
    } finally {
      setProcessing(false);
    }
  };

  const handleDirectLogin = () => {
    setModalType('direct');
    setShowLoginModal(true);
  };

  const validateModalForm = () => {
    const errors = {};
    if (!modalEmail) errors.email = 'Email required';
    else if (!/\S+@\S+\.\S+/.test(modalEmail)) errors.email = 'Invalid email';
    if (!modalPassword) errors.password = 'Password required';
    return errors;
  };

  const modalLoginForm = async (e) => {
    e.preventDefault();
    const errors = validateModalForm();
    setModalFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setProcessing(true);
    setDisabled(true);
    try {
      const response = await axios.post(LoginUrl, {
        email: modalEmail, password: modalPassword, browser: true, user_type: 'participant'
      });
      if (response.status === 200 && response.data.serverstatus !== 400) {
        handleParticipantLoginSuccess(response.data, modalType === 'passcode');
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      toast.error(error.response?.status === 401 ? 'Invalid password' : 'Login failed');
    } finally {
      setProcessing(false);
      setDisabled(false);
    }
  };

  const modalGoogleSuccess = async (response) => {
    try {
      jwtDecode(response.credential);
      setProcessing(true);
      setDisabled(true);
      const res = await axios.post(gmailLogin, {
        token: response.credential, browser: true, user_type: 'participant'
      });
      if (res.status === 200) handleParticipantLoginSuccess(res.data, modalType === 'passcode');
      else toast.error(res.data?.message || 'Google login failed');
    } catch {
      toast.error('Google authentication failed');
    } finally {
      setProcessing(false);
      setDisabled(false);
    }
  };

  return (
    <>
      <Helmet><title>Participant Login | CBTHost</title></Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-lg mb-4">
                <i className="fas fa-users text-white text-2xl"></i>
              </div>
              <h2 className="text-3xl font-extrabold bg-gradient-to-r from-emerald-700 to-teal-700 bg-clip-text text-transparent">
                CBTHost Participant
              </h2>
              <p className="text-gray-500 mt-2">Take tests or view your scores</p>
            </div>

            {/* Passcode Section */}
            <div className="bg-blue-50 rounded-2xl p-5 mb-8 border border-blue-200">
              <div className="flex items-center mb-3">
                <i className="fas fa-key text-blue-600 text-lg mr-2"></i>
                <h3 className="font-semibold text-blue-800">Join a Test</h3>
              </div>
              <form onSubmit={handleParticipate} className="space-y-3">
                <input type="text" value={passcode} onChange={e => setPasscode(e.target.value.toUpperCase())}
                  placeholder="Enter test passcode (e.g., cbthost-pssi-xxxx)"
                  className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 font-mono" />
                <button type="submit" disabled={processing}
                  className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold py-3 rounded-xl transition transform hover:-translate-y-0.5">
                  {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Validating...</> : <><i className="fas fa-play-circle mr-2"></i>Validate & Join</>}
                </button>
              </form>
            </div>

            {/* Direct Dashboard Access */}
            <button onClick={handleDirectLogin}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-md transition transform hover:-translate-y-0.5">
              <i className="fas fa-chart-line mr-2"></i>View My Dashboard & Scores
            </button>

{/* footer */}
{/* Footer Links - Clean Minimalist */}
<div className="mt-8 pt-6 border-t border-gray-200">
  {/* Creator Link - Highlighted */}
  <div className="text-center mb-4">
    <a href="https://creator.cbthost.com/login" className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg hover:from-emerald-100 hover:to-teal-100 transition group">
      <i className="fas fa-chalkboard-user text-emerald-600 mr-2"></i>
      <span className="text-sm font-medium text-emerald-700">Instructor/Creator? Access Creator Portal →</span>
    </a>
  </div>

  {/* Main Links - Horizontal on desktop, vertical on mobile */}
  <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-8">
    <a href="/sign-up" className="flex items-center text-sm text-gray-500 hover:text-blue-600 transition">
      <i className="fas fa-user-plus text-gray-400 mr-2"></i>
      Sign Up
    </a>
    
    <div className="hidden sm:block w-1 h-1 bg-gray-300 rounded-full"></div>
    
    <a href="/password-reset" className="flex items-center text-sm text-gray-500 hover:text-blue-600 transition">
      <i className="fas fa-key text-gray-400 mr-2"></i>
      Reset Password
    </a>
    
    <div className="hidden sm:block w-1 h-1 bg-gray-300 rounded-full"></div>
    
    <a href="https://cbthost.com/support" className="flex items-center text-sm text-gray-500 hover:text-blue-600 transition" target="_blank" rel="noopener noreferrer">
      <i className="fas fa-headset text-gray-400 mr-2"></i>
      Support
    </a>
    
    <div className="hidden sm:block w-1 h-1 bg-gray-300 rounded-full"></div>
    
    {/* <a href="https://cbthost.com/enterprise" className="flex items-center text-sm text-gray-500 hover:text-emerald-600 transition" target="_blank" rel="noopener noreferrer">
      <i className="fas fa-building text-gray-400 mr-2"></i>
      Enterprise
    </a> */}
  </div>

  {/* Copyright */}
  <p className="text-center text-xs text-gray-400 mt-6 pt-3 border-t border-gray-100">
    © {new Date().getFullYear()} CBTHost.com
  </p>
</div>
{/* footer */}
          
          </div>
        </div>
      </div>

      {/* Login Modal (same logic as original) */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75 backdrop-blur-sm" onClick={() => setShowLoginModal(false)}></div>
            <div className="relative bg-white rounded-2xl max-w-md w-full shadow-2xl transform transition-all">
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white">{modalType === 'passcode' ? 'Join Test' : 'Participant Login'}</h3>
                  <button onClick={() => setShowLoginModal(false)} className="text-white hover:text-gray-200"><i className="fas fa-times"></i></button>
                </div>
                <p className="text-blue-100 text-sm">{modalType === 'passcode' ? 'Passcode validated. Login to continue.' : 'Login to access your dashboard.'}</p>
              </div>
              <div className="p-6">
                {modalType === 'passcode' && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg text-center">
                    <span className="font-mono font-bold text-blue-800">{pendingPasscode}</span>
                  </div>
                )}
                <div className="flex justify-center mb-6">
                  <GoogleLogin onSuccess={modalGoogleSuccess} onError={() => toast.error('Google login failed')} theme="filled_blue" size="large" shape="pill" width="100%" />
                </div>
                <div className="relative my-4"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300"></div></div><div className="relative flex justify-center text-sm"><span className="px-3 bg-white text-gray-500">Or email</span></div></div>
                <form onSubmit={modalLoginForm} className="space-y-4">
                  <input type="email" placeholder="Email" value={modalEmail} onChange={e => setModalEmail(e.target.value)}
                    className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500" />
                  {modalFormErrors.email && <p className="text-red-500 text-sm">{modalFormErrors.email}</p>}
                  <input type="password" placeholder="Password" value={modalPassword} onChange={e => setModalPassword(e.target.value)}
                    className="w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-blue-500" />
                  {modalFormErrors.password && <p className="text-red-500 text-sm">{modalFormErrors.password}</p>}
                  <button type="submit" disabled={disabled} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-semibold">
                    {processing ? <><i className="fas fa-spinner fa-spin mr-2"></i>Logging in...</> : <><i className="fas fa-sign-in-alt mr-2"></i>{modalType === 'passcode' ? 'Login & Start Test' : 'Login to Dashboard'}</>}
                  </button>
                </form>
                <div className="mt-4 text-center text-sm"><a href="/password-reset" className="text-blue-600">Forgot password?</a> | <a href="/sign-up" className="text-blue-600">Create account</a></div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer position="top-center" theme="colored" />
    </>
  );
};

export default ParticipantLogin;